import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Lenis from "lenis";
import "@/App.css";

import ProgressBar from "@/components/site/ProgressBar";
import Header from "@/components/site/Header";
import Hero from "@/components/site/Hero";
import SocialProof from "@/components/site/SocialProof";
import Scorecard from "@/components/site/Scorecard";
import LeadGate from "@/components/site/LeadGate";
import ResultReveal from "@/components/site/ResultReveal";
import Framework from "@/components/site/Framework";
import Benchmarks from "@/components/site/Benchmarks";
import CTA from "@/components/site/CTA";
import Footer from "@/components/site/Footer";

import { QUESTIONS, getBand } from "@/data/assessment";
import {
  fireFbqCompleted,
  fireWebhook,
  initPageView,
  initScrollDepthTracking,
  pushDataLayer,
} from "@/lib/tracking";

// GHL calendar booking URL — opened in a new tab from both diagnostic CTA buttons.
const GHL_CALENDAR_URL = "https://api.leadconnectorhq.com/widget/booking/QSx3UaShQzizuBcfu1TK";

function App() {
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("idle"); // idle | gate | loading | result
  const [score, setScore] = useState(0);

  const lenisRef = useRef(null);
  const utmRef = useRef({});
  const gateRef = useRef(null);
  const resultRef = useRef(null);

  // Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  // Tracking init (page_view + UTM + scroll depth)
  useEffect(() => {
    utmRef.current = initPageView();
    const teardown = initScrollDepthTracking();
    return teardown;
  }, []);

  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / QUESTIONS.length) * 100;
  const band = useMemo(() => getBand(score), [score]);

  const handleSelect = useCallback((qId, key) => {
    setAnswers((prev) => ({ ...prev, [qId]: key }));
  }, []);

  const smoothScrollTo = useCallback((target, offset = -64) => {
    if (!target) return;
    if (lenisRef.current) {
      lenisRef.current.resize();
      lenisRef.current.scrollTo(target, { offset, duration: 1.3 });
    } else {
      target.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleCalculate = useCallback(() => {
    if (Object.keys(answers).length !== QUESTIONS.length) return;
    const total = QUESTIONS.reduce((sum, q) => {
      const chosen = q.options.find((o) => o.key === answers[q.id]);
      return sum + (chosen ? chosen.score : 0);
    }, 0);
    setScore(total);
    setPhase("gate");

    pushDataLayer({ event: "calculate_score_click" });

    setTimeout(() => smoothScrollTo(gateRef.current), 220);
  }, [answers, smoothScrollTo]);

  // Map answers by question NUMBER (Q01..Q07) so the webhook payload matches
  // the "q1..q7" contract regardless of internal ids.
  const answersByNumber = useMemo(() => {
    const out = {};
    QUESTIONS.forEach((q, i) => {
      out[`q${i + 1}`] = answers[q.id] || "";
    });
    return out;
  }, [answers]);

  const handleGateSubmit = useCallback(
    ({ name, website, email, whatsapp, budget }) => {
      const currentBand = getBand(score);
      const timestamp = new Date().toISOString();
      const utm = utmRef.current || {};

      // Fire webhook + pixel + dataLayer BEFORE loading transition.
      fireWebhook({
        name,
        website,
        email,
        whatsapp,
        monthly_ad_budget: budget,
        lead_magnet: "Patient Quality Scorecard",
        score,
        band: currentBand.name,
        fbclid: utm.fbclid || "",
        timestamp,
        utm: {
          source: utm.source || "",
          medium: utm.medium || "",
          campaign: utm.campaign || "",
          content: utm.content || "",
          term: utm.term || "",
        },
        answers: answersByNumber,
      });

      fireFbqCompleted(score, currentBand.name);

      pushDataLayer({
        event: "scorecard_completed",
        score,
        band: currentBand.name,
        monthly_ad_budget: budget,
        website,
      });

      setPhase("loading");
      setTimeout(() => {
        setPhase("result");
        setTimeout(() => smoothScrollTo(resultRef.current), 120);
      }, 1500);
    },
    [score, answersByNumber, smoothScrollTo],
  );

  const handleCalendarClick = useCallback((location) => {
    pushDataLayer({
      event: "calendar_button_click",
      button_location: location, // 'result_section' | 'cta_section'
    });
    if (typeof window !== "undefined") {
      window.open(GHL_CALENDAR_URL, "_blank");
    }
  }, []);

  return (
    <div className="App">
      <ProgressBar progress={progress} />
      <Header />
      <Hero />
      <SocialProof />
      <Scorecard
        answers={answers}
        onSelect={handleSelect}
        answeredCount={answeredCount}
        onCalculate={handleCalculate}
      />

      {(phase === "gate" || phase === "loading") && (
        <LeadGate
          ref={gateRef}
          onSubmit={handleGateSubmit}
          loading={phase === "loading"}
        />
      )}

      {phase === "result" && (
        <>
          <ResultReveal
            ref={resultRef}
            score={score}
            band={band}
            visible
            onCalendarClick={handleCalendarClick}
          />
          <Framework />
          <Benchmarks />
          <CTA onCalendarClick={handleCalendarClick} />
          <Footer />
        </>
      )}
    </div>
  );
}

export default App;
